import { gql } from '@apollo/client';
import { apolloClient } from '../apollo/client';
import type {
  PedidoReembolso,
  PedidoReembolsoAuditoria,
  SolicitarReembolsoInput,
  AlterarStatusReembolsoInput,
} from '../types/pedidoReembolso';

// ---------------------------------------------------------------------------
// Fragments
// ---------------------------------------------------------------------------

export const PEDIDO_REEMBOLSO_FIELDS = `
  id
  valorTotal
  status
  invoiceId
  motivoSolicitacao
  motivoRecusa
  observacaoOrganizador
  meioPagamentoOriginal
  iuguRefundId
  criadoEm
  atualizadoEm
  criadoPor {
    id
    nome
    email
    imagemUrl
  }
  atualizadoPor {
    id
    nome
    email
    imagemUrl
  }
  pagamentoInscricao {
    id
    valor
    valorSemDesconto
    descontoAplicado
    status
    dataPagamento
    meioPagamento
    codigoTransacao
    invoiceId
    reciboUrl
    criadoEm
    dataVencimento
    dataCancelamento
    motivoCancelamento
    cupomAplicado {
      codigo
      valor
      quantidadeDisponivel
      tipo
      status
      criadoEm
      atualizadoEm
    }
    inscricoes {
      id
      status
      categoriaTorneio {
        id
        nome
        torneio { id nome ligaId }
      }
      equipe {
        id
        nome
        atletas {
          id
          usuario { id nome email imagemUrl }
        }
      }
    }
  }
`;

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export const PEDIDO_REEMBOLSO = gql`
  query PedidoReembolso($id: ID!) {
    pedidoReembolso(id: $id) {
      ${PEDIDO_REEMBOLSO_FIELDS}
    }
  }
`;

export const PEDIDOS_REEMBOLSO_POR_PAGAMENTO = gql`
  query PedidosReembolsoPorPagamento($pagamentoId: ID!) {
    pedidosReembolsoPorPagamento(pagamentoId: $pagamentoId) {
      ${PEDIDO_REEMBOLSO_FIELDS}
    }
  }
`;

// meusPedidosReembolso: recorte pelo token (guard S-1) — sem parâmetro de identificação,
// a query do backend nem aceitaria um (financeiro-dashboard-atleta Phase 5, Task 1 do plano).
export const MEUS_PEDIDOS_REEMBOLSO = gql`
  query MeusPedidosReembolso($page: Int, $size: Int) {
    meusPedidosReembolso(page: $page, size: $size) {
      items { ${PEDIDO_REEMBOLSO_FIELDS} }
      page size totalItems totalPages
    }
  }
`;

// reembolsosPorLiga (Q-25, entregue pela Phase 2, ver
// PRPs/reports/financeiro-dashboard-liga-torneio/grounding-schema-fase-4-5.md) — `ligaId` é o
// único argumento obrigatório. Seleção própria (não reusa PEDIDO_REEMBOLSO_FIELDS): os 11 campos
// escalares + `criadoPor { id nome }`/`atualizadoPor { id nome }` que
// components/financeiro/PedidoReembolsoCard.tsx de fato lê (13 dos 14 campos do tipo);
// `pagamentoInscricao` fica de fora por não ser consumido pelo card nesta fase. Reusa o envelope
// PedidoReembolsoConnection já existente (items/page/size/totalItems/totalPages).
export const REEMBOLSOS_POR_LIGA = gql`
  query ReembolsosPorLiga(
    $ligaId: ID!
    $status: [StatusReembolso!]
    $inicio: Date
    $fim: Date
    $page: Int
    $size: Int
  ) {
    reembolsosPorLiga(
      ligaId: $ligaId
      status: $status
      inicio: $inicio
      fim: $fim
      page: $page
      size: $size
    ) {
      items {
        id
        valorTotal
        status
        invoiceId
        motivoSolicitacao
        motivoRecusa
        observacaoOrganizador
        meioPagamentoOriginal
        iuguRefundId
        criadoEm
        atualizadoEm
        criadoPor {
          id
          nome
        }
        atualizadoPor {
          id
          nome
        }
      }
      page
      size
      totalItems
      totalPages
    }
  }
`;

// filaReembolsosPlataforma (Q-27, AC-6, entregue pela Phase 3 -- ver
// inplay-back/src/main/resources/graphql/pedidoReembolso.graphqls:36-56, confirmado por leitura
// direta nesta fase, Task 1): fila global ordenada por idade (mais antigo primeiro), sem
// $ligaId (a query é global, ao contrário de reembolsosPorLiga). Reusa PEDIDO_REEMBOLSO_FIELDS
// para o `pedido` de cada item, mais `idadeDias` (calculada no backend a partir de `criadoEm`) e
// `contadores { status quantidade }` -- non-null-list-of-non-null, sempre 4 entradas, lido
// DIRETO pelo hook consumidor (hooks/useFilaReembolsosPlataforma.ts), nunca `.filter`/`.reduce`
// sobre `items` (docs/anti-patterns.md, "Contar itens da página carregada").
export const FILA_REEMBOLSOS_PLATAFORMA = gql`
  query FilaReembolsosPlataforma(
    $status: [StatusReembolso!]
    $inicio: Date
    $fim: Date
    $page: Int
    $size: Int
  ) {
    filaReembolsosPlataforma(
      status: $status
      inicio: $inicio
      fim: $fim
      page: $page
      size: $size
    ) {
      items {
        pedido { ${PEDIDO_REEMBOLSO_FIELDS} }
        idadeDias
      }
      page
      size
      totalItems
      totalPages
      contadores { status quantidade }
    }
  }
`;

export const AUDITORIAS_PEDIDO_REEMBOLSO = gql`
  query AuditoriasPedidoReembolso($pedidoReembolsoId: ID!) {
    auditoriasPedidoReembolso(pedidoReembolsoId: $pedidoReembolsoId) {
      id
      acao
      dadosAnteriores
      dadosNovos
      realizadoEm
      realizadoPor {
        id
        nome
        email
        imagemUrl
      }
    }
  }
`;

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export const SOLICITAR_REEMBOLSO = gql`
  mutation SolicitarReembolso($input: SolicitarReembolsoInput!) {
    solicitarReembolso(input: $input) {
      ${PEDIDO_REEMBOLSO_FIELDS}
    }
  }
`;

export const ALTERAR_STATUS_REEMBOLSO = gql`
  mutation AlterarStatusReembolso($input: AlterarStatusReembolsoInput!) {
    alterarStatusReembolso(input: $input) {
      ${PEDIDO_REEMBOLSO_FIELDS}
    }
  }
`;

export const EXECUTAR_REEMBOLSO = gql`
  mutation ExecutarReembolso($pedidoId: ID!) {
    executarReembolso(pedidoId: $pedidoId) {
      ${PEDIDO_REEMBOLSO_FIELDS}
    }
  }
`;

export const FINALIZAR_REEMBOLSO = gql`
  mutation FinalizarReembolso($pedidoId: ID!) {
    finalizarReembolso(pedidoId: $pedidoId) {
      ${PEDIDO_REEMBOLSO_FIELDS}
    }
  }
`;

// ---------------------------------------------------------------------------
// Data/Vars interfaces
// ---------------------------------------------------------------------------

interface PedidoReembolsoData {
  pedidoReembolso: PedidoReembolso;
}

interface PedidosReembolsoPorPagamentoData {
  pedidosReembolsoPorPagamento: PedidoReembolso[];
}

export interface MeusPedidosReembolsoConnection {
  items: PedidoReembolso[];
  page: number;
  size: number;
  totalItems: number;
  totalPages: number;
}

export interface MeusPedidosReembolsoData {
  meusPedidosReembolso: MeusPedidosReembolsoConnection | null;
}

export interface MeusPedidosReembolsoVars {
  page: number;
  size: number;
}

interface AuditoriasPedidoReembolsoData {
  auditoriasPedidoReembolso: PedidoReembolsoAuditoria[];
}

// financeiro-dashboard-plataforma Phase 5 (Task 3) -- tipos de FILA_REEMBOLSOS_PLATAFORMA.
export interface FilaReembolsoItemData {
  pedido: PedidoReembolso;
  idadeDias: number;
}

export interface ContadorStatusReembolsoData {
  status: string;
  quantidade: number;
}

export interface FilaReembolsosPlataformaConnection {
  items: FilaReembolsoItemData[] | null;
  page: number;
  size: number;
  totalItems: number;
  totalPages: number;
  contadores: ContadorStatusReembolsoData[];
}

export interface FilaReembolsosPlataformaData {
  filaReembolsosPlataforma: FilaReembolsosPlataformaConnection | null;
}

export interface FilaReembolsosPlataformaVars {
  status?: string[];
  inicio?: string;
  fim?: string;
  page?: number;
  size?: number;
}

interface SolicitarReembolsoData {
  solicitarReembolso: PedidoReembolso;
}

interface AlterarStatusReembolsoData {
  alterarStatusReembolso: PedidoReembolso;
}

interface ExecutarReembolsoData {
  executarReembolso: PedidoReembolso;
}

interface FinalizarReembolsoData {
  finalizarReembolso: PedidoReembolso;
}

// ---------------------------------------------------------------------------
// Service functions — Queries
// ---------------------------------------------------------------------------

export const buscarPedidoReembolso = async (id: string): Promise<PedidoReembolso> => {
  const { data } = await apolloClient.query<PedidoReembolsoData>({
    query: PEDIDO_REEMBOLSO,
    variables: { id },
    fetchPolicy: 'no-cache',
  });
  return data.pedidoReembolso;
};

export const buscarPedidosReembolsoPorPagamento = async (
  pagamentoId: string,
): Promise<PedidoReembolso[]> => {
  const { data } = await apolloClient.query<PedidosReembolsoPorPagamentoData>({
    query: PEDIDOS_REEMBOLSO_POR_PAGAMENTO,
    variables: { pagamentoId },
    fetchPolicy: 'no-cache',
  });
  return data.pedidosReembolsoPorPagamento;
};

export const buscarMeusPedidosReembolso = async (
  page = 0,
  size = 10,
): Promise<MeusPedidosReembolsoConnection | null> => {
  const { data } = await apolloClient.query<MeusPedidosReembolsoData, MeusPedidosReembolsoVars>({
    query: MEUS_PEDIDOS_REEMBOLSO,
    variables: { page, size },
    fetchPolicy: 'no-cache',
  });
  return data.meusPedidosReembolso;
};

export const buscarAuditoriasPedidoReembolso = async (
  pedidoReembolsoId: string,
): Promise<PedidoReembolsoAuditoria[]> => {
  const { data } = await apolloClient.query<AuditoriasPedidoReembolsoData>({
    query: AUDITORIAS_PEDIDO_REEMBOLSO,
    variables: { pedidoReembolsoId },
    fetchPolicy: 'no-cache',
  });
  return data.auditoriasPedidoReembolso;
};

// ---------------------------------------------------------------------------
// Service functions — Mutations
// ---------------------------------------------------------------------------

export const solicitarReembolso = async (
  input: SolicitarReembolsoInput,
): Promise<PedidoReembolso> => {
  const { data, errors } = await apolloClient.mutate<SolicitarReembolsoData>({
    mutation: SOLICITAR_REEMBOLSO,
    variables: { input },
    fetchPolicy: 'no-cache',
  });
  if (errors && errors.length) {
    throw new Error(errors[0].message);
  }
  if (!data?.solicitarReembolso) {
    throw new Error('Resposta invalida ao solicitar reembolso.');
  }
  return data.solicitarReembolso;
};

export const alterarStatusReembolso = async (
  input: AlterarStatusReembolsoInput,
): Promise<PedidoReembolso> => {
  const { data, errors } = await apolloClient.mutate<AlterarStatusReembolsoData>({
    mutation: ALTERAR_STATUS_REEMBOLSO,
    variables: { input },
    fetchPolicy: 'no-cache',
  });
  if (errors && errors.length) {
    throw new Error(errors[0].message);
  }
  if (!data?.alterarStatusReembolso) {
    throw new Error('Resposta invalida ao alterar status do reembolso.');
  }
  return data.alterarStatusReembolso;
};

export const executarReembolso = async (pedidoId: string): Promise<PedidoReembolso> => {
  const { data, errors } = await apolloClient.mutate<ExecutarReembolsoData>({
    mutation: EXECUTAR_REEMBOLSO,
    variables: { pedidoId },
    fetchPolicy: 'no-cache',
  });
  if (errors && errors.length) {
    throw new Error(errors[0].message);
  }
  if (!data?.executarReembolso) {
    throw new Error('Resposta invalida ao executar reembolso.');
  }
  return data.executarReembolso;
};

export const finalizarReembolso = async (pedidoId: string): Promise<PedidoReembolso> => {
  const { data, errors } = await apolloClient.mutate<FinalizarReembolsoData>({
    mutation: FINALIZAR_REEMBOLSO,
    variables: { pedidoId },
    fetchPolicy: 'no-cache',
  });
  if (errors && errors.length) {
    throw new Error(errors[0].message);
  }
  if (!data?.finalizarReembolso) {
    throw new Error('Resposta invalida ao finalizar reembolso.');
  }
  return data.finalizarReembolso;
};
